import { validateEmail } from '@/utils'

import { IInviteUser } from '../types'

/**
 * Check for duplicate emails within the current batch of users (case-insensitive).
 * Does NOT check against database - API handles that.
 *
 * @param users - Array of user entries to check
 * @returns Map of normalized (lowercase) email -> true if duplicate
 *
 * @example
 * const users = [
 *   { email: 'user1@test.com', ... },
 *   { email: 'user2@test.com', ... },
 *   { email: 'User1@Test.com', ... }, // duplicate (same as first)
 * ];
 * const duplicates = checkDuplicateEmailsInBatch(users);
 * duplicates.get('user1@test.com') // true
 * duplicates.get('user2@test.com') // false
 */
export const checkDuplicateEmailsInBatch = (
  users: IInviteUser[]
): { duplicates: Map<string, boolean>; emailCounts: Map<string, number> } => {
  const emailCounts = new Map<string, number>()
  const duplicates = new Map<string, boolean>()

  // Count occurrences of each email (case-insensitive)
  users.forEach((user) => {
    if (user.email && user.email.trim() !== '') {
      const normalizedEmail = user.email.toLowerCase().trim()
      const count = emailCounts.get(normalizedEmail) || 0
      emailCounts.set(normalizedEmail, count + 1)
    }
  })

  // Mark emails that appear more than once as duplicates (key by normalized email for consistent lookup)
  emailCounts.forEach((count, normalizedEmail) => {
    if (count > 1) duplicates.set(normalizedEmail, true)
  })

  return { duplicates, emailCounts }
}

/**
 * Combined email error: format validation + duplicate-in-batch check.
 * Use this for inline email field validation when you have batch duplicate data.
 *
 * @param email - Email address to validate
 * @param duplicateEmailsInBatch - Map of normalized (lowercase) email -> true if duplicated in current batch
 * @returns Error message if invalid or duplicate, empty string if valid
 */
export const getEmailError = (
  email: string,
  duplicateEmailsInBatch: Map<string, boolean>
): string => {
  if (!email || email.trim() === '') return ''

  const formatError = validateEmail(email)
  if (formatError) return formatError

  const normalizedEmail = email.toLowerCase().trim()
  const isDuplicate = duplicateEmailsInBatch.get(normalizedEmail)

  if (isDuplicate) return 'This email is duplicated'
  return ''
}
